import React from "react";
import { SectionCard } from "../study/course/SectionCard";
import Text from "../renderer/Text";
import AudioRecorder from "../study/course/AudioRecorder";
import { makeStyles } from "tss-react/mui";
import Button from "../Button";
import { useStudy } from "../../pages/study/hooks";
import { addAssignmentActivitiesByCourseAndAssignee } from "../../store/study/studyBaseApi";
import { updateSpeakAloudActivity, nowISO } from "../../utils";
import CompleteChapterButton from "./CompleteChapterButton";
import { useCourse } from "../../pages/course/hooks";

const useStyles = makeStyles()((theme) => ({
    actionBtn: {
        marginBottom: theme.spacing(2),
        justifyContent: "center",
        display: "flex"
    },
    textContainer: {
        height: 'calc(100vh - 475px)',
        overflowY: 'scroll',
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#F5F5F5',
            borderRadius: '10px',
        },
        '&:hover, &:focus-within': {
            '&::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(193.28deg, #29ABE2 5.42%, #2DA2E2 12.18%, #398CE2 33.81%, #417EE2 54.77%, #447AE2 73.02%)',
                width: '12px',
                cursor: 'pointer',
            },
        },
    },
}));

export default function Story({ data, sectionId, courseId, onComplete, onNextChapter }) {
    const { classes } = useStyles();
    const { profile } = useStudy();
    const { lastSectionChapter } = useCourse();
    const [chapter, setChapter] = React.useState({});
    const [audioRecorded, setAudioRecorded] = React.useState(false);
    const [completed, setCompleted] = React.useState(false);
    const startTime = React.useRef(null);

    const onSpeechRecordingCompletion = (audioBlob) => {
        setAudioRecorded(true);

        const speechInfo = {
            audioUrl: audioBlob,
            text: chapter.content,
            outcome: {},
            score: 0,
        }

        updateSpeakAloudActivity({
            speechList: [speechInfo],
            profileId: profile.id,
            chapterId: chapter.chapterId,
            sectionId,
            start: startTime.current,
            end: nowISO(),
            courseId,
            type: 'STORY_READING',
        })

    }

    const handleStart = () => {
        if (startTime.current) return;

        startTime.current = nowISO();
    }

    const handleComplete = async () => {
        setCompleted(true);

        try {
            await addAssignmentActivitiesByCourseAndAssignee({
                params: { courseId, assigneeId: profile.id }, body: {
                    assigneeId: profile.id, courseId, sectionId,
                    chapterId: chapter.chapterId,
                    endTime: nowISO(),
                    startTime: startTime.current,
                    type: 'CHAPTER_COMPLETE',
                }
            });
        } catch (e) {
            console.error(e);
        }

        // Signal completion only AFTER the CHAPTER_COMPLETE write above —
        // the session-gate verdict (retake vs celebrate) refetches the gate
        // and must see the finished attempt.
        if (onComplete && typeof onComplete === "function") {
            onComplete();
        }

        try {
            if (!lastSectionChapter.chapter) {
                await onNextChapter();
            }
        } catch (e) {
            console.error(e);
        }
    }

    React.useEffect(() => {
        setChapter(data);
        setCompleted(false);
        setAudioRecorded(false);
        // Reset the per-session start timestamp so the next session captures its own
        // start (handleStart's `if (startTime.current) return` guard would otherwise
        // keep the previous session's value and post it as this session's startTime).
        startTime.current = null;
    }, [data]);

    if (!Object.keys(chapter).length) return <></>;

    return (
        <React.Fragment>
            <SectionCard style={{
                height: 'calc(100vh - 475px)',
                overflowY: 'scroll',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#F5F5F5',
                    borderRadius: '10px',
                },
                '&:hover, &:focus-within': {
                    '&::-webkit-scrollbar-thumb': {
                        background: 'linear-gradient(193.28deg, #29ABE2 5.42%, #2DA2E2 12.18%, #398CE2 33.81%, #417EE2 54.77%, #447AE2 73.02%)',
                        width: '12px',
                        cursor: 'pointer',
                    },
                },
            }}>
                <Text content={chapter?.content} />
            </SectionCard>
            <SectionCard>
                <AudioRecorder key={chapter.chapterId} onStart={handleStart} onComplete={onSpeechRecordingCompletion} sampleRate={16000} />
            </SectionCard>

            <div className={classes.actionBtn}>
                {!completed && <CompleteChapterButton onClick={handleComplete} disabled={!audioRecorded} />}
            </div>
        </React.Fragment>
    )
}